import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppRoute } from 'app/app-layout/constants/app-route.enum';
import { AppStateInterface } from 'app/core/app-config/state/models/app-state.interface';
import { RouterRequestInterface } from 'app/core/browser/models/router-request.model';
import { BrowserServiceInterface } from 'app/core/browser/services/browser-service.interface';
import { RouterStateUrlInterface } from 'app/core/browser/state/models/router-state-url.interface';
import { DashboardAccordionIndexKey } from 'app/my-zone/home/constants/dashboard-accordion-index-key';
import { DashboardAccordionIndexEnum } from 'app/my-zone/home/constants/dashboard-accordion-index.enum';
import { DashboardTabIndexKey } from 'app/my-zone/home/constants/dashboard-tab-index-key';
import { DashboardTabIndexEnum } from 'app/my-zone/home/constants/dashboard-tab-index.enum';
import { from, Observable } from 'rxjs';

@Injectable()
export class BrowserService implements BrowserServiceInterface {

  constructor(
    private readonly _location: Location,
    private readonly _router: Router,
    private readonly _store: Store<AppStateInterface>) { }

  /**
   * Go to specified route.
   *
   * @param route The specified route
   *
   * @returns Observable flag indicating if navigation was successful
   */
  public to({ route, routeParams, queryParams }: RouterRequestInterface): Observable<boolean> {
    return from(this._router.navigate(routeParams ? [route, ...routeParams] : [route], { queryParams }));
  }

  /**
   * Navigate to previous route.
   */
  public back(): void {
    this._location.back();
  }

  /**
   * Navigate to next route
   */
  public forward(): void {
    this._location.forward();
  }

  /**
   * Get the current browser location path.
   */
  public getPath(): string {
    return this._location.path();
  }

  /**
   * Adds a / to a path if none exists.
   *
   * @param path Path to normalize
   */
  public normalize(path: string): string {
    const normalized: string = this._location.normalize(path);
    return normalized && normalized.charAt(0) === '/' ? normalized.substr(1) : normalized;
  }

  /**
   * Compare the provided path with the current location path.
   *
   * @param path Path to compare with location path
   * @param query Query parameters
   */
  public isCurrentPathEqualTo(path: string, query: string = ''): boolean {
    return this._location.isCurrentPathEqualTo(path, query);
  }


  /**
   * Determine the primary AppRoute path that corresponds with the provided full path.
   *
   * @param path Path to compare with known routes
   */
  public determinePrimaryNavAppRoute(path: string): AppRoute {
    const normalizedPath: string = this.normalize(path);
    for (const route of Object.getOwnPropertyNames(AppRoute)) {
      const routeValue: AppRoute = AppRoute[route];
      const normalizedRoute: string = this.normalize(routeValue);

      // The AppRoute.home value is empty, so will match every time. Skip here and default to home.
      if(normalizedRoute.length > 0 && normalizedPath.startsWith(this.normalize(routeValue))) {
        return routeValue;
      }
    }

    return AppRoute.home;
  }

  /**
   * Retrieve an Observable stream of current Angular Router state.
   */
  public selectRouterState(): Observable<Readonly<RouterStateUrlInterface>> {
    return this._store.select<RouterStateUrlInterface>(({ router }: AppStateInterface) => router && router.state);
  }

  /**
   * Navigate to the dashboard
   *
   * @param selectedTabIndex Tab to select upon landing
   * @param selectedAccordionIndex According to expand upon landing
   */
  public toDashboard(selectedTabIndex?: DashboardTabIndexEnum, selectedAccordionIndex?: DashboardAccordionIndexEnum): Observable<boolean> {
    return this.to({
      route: AppRoute.dashboard,
      routeParams: [{
        [DashboardTabIndexKey.tabIndex]: selectedTabIndex,
        [DashboardAccordionIndexKey.accordionIndex]: selectedAccordionIndex,
      }],
    });
  }

  public toExecutiveSummary(): Observable<boolean> {
    return this.to({ route: AppRoute.executiveSummary });
  }

  public toLogin(): Observable<boolean> {
    return this.to({ route: AppRoute.login });
  }

  /**
   * Navigate to home view
   *
   * @returns Observable flag indicating if navigation was successful
  */
  public toHome(): Observable<boolean> {
    return this.to({ route: AppRoute.home });
  }

  public toManagementOverview(): Observable<boolean> {
    return this.to({ route: AppRoute.managementOverview });
  }

  public toSystemUsers(): Observable<boolean> {
    return this.to({ route: AppRoute.systemUsers });
  }

  /**
   * Navigate to Survey view
   *
   * @returns Observable flag indicating if navigation was successful
  */
  public toSurvey(queryParams: any = null): Observable<boolean> {
    if (queryParams) {
      return this.to({ queryParams, route: AppRoute.survey });
    }

    return this.to({ route: AppRoute.survey });
  }

  /**
   * Navigate to Evaluation view
   *
   * @returns Observable flag indicating if navigation was successful
  */
  public toEvaluation(): Observable<boolean> {
    return this.to({ route: AppRoute.evaluation });
  }

  /**
   * Navigate to User Details
   *
   * @returns Observable flag indicating if navigation was successful
  */
  public toUserDetails(): Observable<boolean> {
    return this.to({ route: `${AppRoute.systemUsers}/${AppRoute.userDetails}` as AppRoute });
  }
}
